import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Alert, Button, Col, Empty, Form, InputNumber, Pagination, Radio, Row, Space, Spin, Steps, Table, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, CalendarOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useDrag, useDrop } from "react-dnd";
import { InputCheck, CancelButton, ModalComponent, SelectWithSearch, SelectResourceList, trans, InputTextArea } from "@comps/components";
import { makeRequestStateless } from "@utils/helpers";
import { createSelectList, sortSelectListByLabel } from "@comps/commonFunctions";
import { bulkCreateDataReq, calculateFormulaReq, getEmployeeFilterReq, getEmployeeListReq, getFormulasReq } from "../requests";
import { Commons, Const } from "../../../constants";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { payrollHelpText } from "../../../payrollHelpText";

const langKey = Const["payitem"].lngKey;

const fieldLabel = makeFieldLabel(payrollHelpText.customPayitem, { namespace: "customPayitem", label: (key) => trans(`${langKey}.${key}`) });
const formName = "createPayitem";
const EMPLOYEE_CARD_TYPE = "custom-payitem-employee-card";

const EmployeeCard = ({ employee, panel, onMove, onAmountChange, showAmountError }) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: EMPLOYEE_CARD_TYPE,
    item: { employee, fromPanel: panel },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [employee, panel]);

  drag(ref);

  return (
    <div
      ref={ ref }
      onClick={ () => onMove(employee, panel === "selected" ? "unselected" : "selected") }
      style={ {
        border: "1px solid #D8E3F0",
        borderRadius: 8,
        background: "#FFFFFF",
        padding: 14,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        cursor: "pointer",
        opacity: isDragging ? 0.45 : 1,
        minHeight: 84,
      } }
    >
      <div
        style={ {
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: "50%",
          background: "#E7F0FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        } }
      >
        <img
          src={ employee.profile_pic_thumb_url && employee.profile_pic_thumb_url !== "images/arnold-avatar.jpg" ? employee.profile_pic_thumb_url : "/images/arnold-avatar.jpg" }
          alt={ employee.full_name_code || employee.full_name || "Employee" }
          style={ { width: "100%", height: "100%", objectFit: "cover" } }
        />
      </div>

      <div style={ { flex: 1, minWidth: 0 } }>
        <div style={ { fontWeight: 600, color: "#223052", marginBottom: 4, wordBreak: "break-word" } }>
          { employee.full_name_code || employee.full_name || employee.employee_name }
        </div>
        <div style={ { color: "#6B7280", fontSize: 12, marginBottom: panel === "selected" ? 10 : 0 } }>
          { employee.designation_name || employee.designation?.name || employee.department_name || "Employee" }
        </div>

        { panel === "selected" && (
          <div onClick={ (event) => event.stopPropagation() }>
            <InputNumber
              className="custom-payitem-amount-input"
              style={ { width: "100%" } }
              min={ 0 }
              value={ employee.amount === "" ? null : Number(employee.amount) }
              placeholder={ "Amount" }
              status={ showAmountError ? "error" : "" }
              onChange={ (value) => onAmountChange(employee.id, value) }
            />
            { showAmountError && (
              <div style={ { color: "#ff4d4f", fontSize: 12, marginTop: 4 } }>
                Amount is required
              </div>
            ) }
          </div>
        ) }
      </div>
    </div>
  );
};

const EmployeePanel = ({ title, count, panel, employees, onMove, onMoveAll, onAmountChange, amountErrors, pagination, onPageChange }) => {
  const ref = useRef(null);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: EMPLOYEE_CARD_TYPE,
    canDrop: (item) => item.fromPanel !== panel,
    drop: (item) => {
      if (item.fromPanel !== panel) {
        onMove(item.employee, panel);
      }
      return { moved: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [panel, onMove]);

  drop(ref);

  return (
    <div
      ref={ ref }
      style={ {
        border: "1px solid #CFE0F3",
        borderRadius: 8,
        minHeight: 360,
        background: isOver && canDrop ? "#F2F8FF" : "#FFFFFF",
        transition: "background-color 0.18s ease, border-color 0.18s ease",
      } }
    >
      <div
        style={ {
          padding: "18px 20px",
          borderBottom: "1px solid #DCE7F3",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        } }
      >
        <Typography.Title level={ 4 } style={ { margin: 0, fontSize: 18, color: "#2A2F3A" } }>
          { title } ({ count })
        </Typography.Title>

        <Button onClick={ onMoveAll } disabled={ employees.length === 0 }>
          { panel === "selected" ? <ArrowLeftOutlined /> : <ArrowRightOutlined /> }
        </Button>
      </div>

      <div
        style={ {
          padding: 16,
          minHeight: 286,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 16,
          alignContent: "start",
        } }
      >
        { employees.length === 0 ? (
          <div style={ { gridColumn: "1 / -1", paddingTop: 24 } }>
            <Empty description={ panel === "selected" ? "No selected employees" : "No unselected employees" } />
          </div>
        ) : employees.map((employee) => (
          <EmployeeCard
            key={ employee.id }
            employee={ employee }
            panel={ panel }
            onMove={ onMove }
            onAmountChange={ onAmountChange }
            showAmountError={ panel === "selected" && amountErrors.has(employee.id) }
          />
        )) }
      </div>

      { panel === "unselected" && pagination && pagination.total > pagination.pageSize && (
        <div style={ { padding: "0 16px 16px", display: "flex", justifyContent: "flex-end" } }>
          <Pagination
            size="small"
            current={ pagination.current }
            pageSize={ pagination.pageSize }
            total={ pagination.total }
            onChange={ onPageChange }
            showSizeChanger={ false }
          />
        </div>
      ) }
    </div>
  );
};

const EffectiveDatesPicker = ({ currentMonth, selectedDates, onMonthChange, onToggleDate, onSelectPreset, onClear }) => {
  const calendarDays = useMemo(() => {
    const start = currentMonth.clone().startOf("month").startOf("week");
    return Array.from({ length: 42 }, (_, index) => start.clone().add(index, "days"));
  }, [currentMonth]);

  const monthLabel = currentMonth.format("MMMM YYYY");
  const selectedSet = new Set(selectedDates);

  return (
    <div>
      <Alert
        type="info"
        showIcon
        icon={ <CalendarOutlined /> }
        message="Pick the days this pay item applies"
        description={ `Only dates in ${monthLabel} can be selected. Formula amounts are calculated per employee and reused for each selected date.` }
        style={ { marginBottom: 16 } }
      />

      <Row gutter={ 16 }>
        <Col lg={ 8 } md={ 10 } xs={ 24 }>
          <div style={ { border: "1px solid #E4E8EF", borderRadius: 8, padding: 14, background: "#FFFFFF" } }>
            <div style={ { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 } }>
              <Typography.Text strong>{ monthLabel }</Typography.Text>
              <Space size={ 8 }>
                <Button icon={ <LeftOutlined /> } onClick={ () => onMonthChange(currentMonth.clone().subtract(1, "month")) } />
                <Button icon={ <RightOutlined /> } onClick={ () => onMonthChange(currentMonth.clone().add(1, "month")) } />
              </Space>
            </div>

            <div style={ { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, textAlign: "center" } }>
              { ["SU", "MO", "TU", "WE", "TH", "FR", "SA"].map((day) => (
                <div key={ day } style={ { fontSize: 11, fontWeight: 700, color: "#9CA3AF", padding: "4px 0" } }>{ day }</div>
              )) }
              { calendarDays.map((day) => {
                const key = day.format("YYYY-MM-DD");
                const inMonth = day.isSame(currentMonth, "month");
                const selected = selectedSet.has(key);
                return (
                  <button
                    type="button"
                    key={ key }
                    disabled={ !inMonth }
                    onClick={ () => onToggleDate(key) }
                    style={ {
                      height: 36,
                      border: selected ? "1px solid #E56B15" : "1px solid transparent",
                      borderRadius: 8,
                      background: selected ? "#E8731A" : "#FFFFFF",
                      color: selected ? "#FFFFFF" : inMonth ? "#1F2937" : "#C7C7C7",
                      fontWeight: selected ? 700 : 500,
                      cursor: inMonth ? "pointer" : "not-allowed",
                    } }
                  >
                    { day.date() }
                  </button>
                );
              }) }
            </div>
          </div>
        </Col>

        <Col lg={ 16 } md={ 14 } xs={ 24 }>
          <div style={ { border: "1px solid #E4E8EF", borderRadius: 8, background: "#FFFFFF", minHeight: 172 } }>
            <div style={ { padding: "12px 14px", borderBottom: "1px solid #EEF0F3", display: "flex", justifyContent: "space-between" } }>
              <Typography.Text strong>Selected dates</Typography.Text>
              <Tag color="orange">{ selectedDates.length }</Tag>
            </div>
            <div style={ { padding: 14, minHeight: 72 } }>
              { selectedDates.length === 0 ? (
                <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No dates selected" />
              ) : (
                <Space wrap>
                  { selectedDates.map((date) => (
                    <Tag
                      key={ date }
                      closable
                      color="orange"
                      onClose={ (event) => {
                        event.preventDefault();
                        onToggleDate(date);
                      } }
                    >
                      { moment(date).format("DD MMM") }
                    </Tag>
                  )) }
                </Space>
              ) }
            </div>
            <div style={ { padding: 14, borderTop: "1px solid #EEF0F3" } }>
              <Space wrap>
                <Button size="small" onClick={ () => onSelectPreset("weekends") }>All weekends</Button>
                <Button size="small" onClick={ () => onSelectPreset("weekdays") }>All weekdays</Button>
                <Button size="small" danger onClick={ onClear } disabled={ selectedDates.length === 0 }>Clear all</Button>
              </Space>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const CreateCustomPayitem = (props) => {
  const [saving, setSaving] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [formulasLoading, setFormulasLoading] = useState(false);
  const [applyingAmount, setApplyingAmount] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(moment().startOf("month"));
  const [selectedDates, setSelectedDates] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    branches: [],
    departments: [],
    grades: [],
    designations: [],
  });
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [amountErrors, setAmountErrors] = useState(new Set());
  const [formulaErrors, setFormulaErrors] = useState([]);
  const [employeePagination, setEmployeePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const selectedLocationId = Form.useWatch("location_id", filterForm);
  const watchedAmountSource = Form.useWatch("amount_source", form);
  const watchedFormulaId = Form.useWatch("formula_id", form);
  const amountSource = watchedAmountSource || form.getFieldValue("amount_source") || "fixed_amount";
  const selectedFormulaId = watchedFormulaId || form.getFieldValue("formula_id");

  const companyId = localStorage.getItem("company_id") ? localStorage.getItem("company_id") : 1;

  useEffect(() => {
    loadFilterOptions();
    loadEmployees({});
    loadFormulas();
    // eslint-disable-next-line
  }, []);

  const branchOptions = useMemo(() => {
    if (!selectedLocationId) {
      return createSelectList(filterOptions.branches);
    }

    return createSelectList(
      (filterOptions.branches || []).filter((branch) => !branch.location_id || branch.location_id === selectedLocationId)
    );
  }, [filterOptions.branches, selectedLocationId]);

  const formulaOptions = useMemo(() => (
    sortSelectListByLabel((formulas || []).map((formula) => ({
      value: formula.id,
      label: `${formula.name || formula.label || "Formula"}${formula.tag ? ` (${formula.tag})` : ""}`,
    })))
  ), [formulas]);

  const selectedEmployeeIds = useMemo(
    () => new Set(selectedEmployees.map((employee) => employee.id)),
    [selectedEmployees]
  );

  const unselectedEmployees = useMemo(
    () => availableEmployees.filter((employee) => !selectedEmployeeIds.has(employee.id)),
    [availableEmployees, selectedEmployeeIds]
  );

  const selectedFormula = useMemo(
    () => formulas.find((formula) => Number(formula.id) === Number(selectedFormulaId)),
    [formulas, selectedFormulaId]
  );

  const previewRows = useMemo(() => {
    const sourceLabel = amountSource === "formula" ? (selectedFormula?.name || "Formula") : "Fixed Amount";
    return selectedEmployees.flatMap((employee) => (
      selectedDates.map((date) => ({
        key: `${employee.id}-${date}`,
        employment_id: employee.id,
        employee_name: employee.full_name_code || employee.full_name || employee.employee_name,
        date,
        amount: employee.amount,
        source: sourceLabel,
        action: amountSource === "formula" ? "Create or update" : "Create new",
      }))
    ));
  }, [amountSource, selectedEmployees, selectedDates, selectedFormula]);

  const mapEmployee = (employee) => ({
    ...employee,
    amount: employee.amount ?? form.getFieldValue("default_amount") ?? "",
  });

  const normalizeFormulaList = (response) => {
    const list = response?.formulas?.data || response?.formulas || response?.data || [];
    return Array.isArray(list) ? list : [];
  };

  const loadFormulas = () => {
    setFormulasLoading(true);
    makeRequestStateless(getFormulasReq, { options: { formulas: true } }, (response) => {
      setFormulas(normalizeFormulaList(response));
      setFormulasLoading(false);
    }, () => {
      setFormulasLoading(false);
      message.error("Unable to load formulas");
    });
  };

  const syncDefaultAmountField = (employees) => {
    if (!employees || employees.length === 0) {
      form.setFieldValue("default_amount", undefined);
      return;
    }

    const normalizedAmounts = employees.map((employee) => (
      employee.amount === "" || employee.amount === null || employee.amount === undefined
        ? ""
        : String(employee.amount)
    ));

    const firstAmount = normalizedAmounts[0];
    const allSame = normalizedAmounts.every((amount) => amount === firstAmount && amount !== "");

    form.setFieldValue("default_amount", allSame ? Number(firstAmount) : undefined);
  };

  const buildFilterPayload = (values = {}, page = 1) => {
    const filters = ["employee_status_|active"];

    if (props.employment_id) {
      filters.push(`ids_|${props.employment_id}`);
    }

    if (values.employment_ids && values.employment_ids.length) {
      filters.push(`ids_|${values.employment_ids.join(",")}`);
    }
    if (values.location_id) {
      filters.push(`location_|${values.location_id}`);
    }
    if (values.designation_id) {
      filters.push(`designation_|${values.designation_id}`);
    }
    if (values.grade_id) {
      filters.push(`grade_|${values.grade_id}`);
    }
    if (values.department_id) {
      filters.push(`department_|${values.department_id}`);
    }
    if (values.branch_id) {
      filters.push(`branch_|${values.branch_id}`);
    }

    return {
      page,
      filters: filters.join(";"),
    };
  };

  const loadFilterOptions = () => {
    setFilterLoading(true);
    makeRequestStateless(getEmployeeFilterReq, null, (response) => {
      setFilterOptions({
        locations: response?.organization_structure_data?.location || [],
        branches: response?.organization_structure_data?.branch || [],
        departments: response?.organization_structure_data?.department || [],
        grades: response?.supporting_data?.grade || [],
        designations: response?.supporting_data?.designation || [],
      });
      setFilterLoading(false);
    }, () => {
      setFilterLoading(false);
    });
  };

  const loadEmployees = (values = {}, page = 1) => {
    setEmployeesLoading(true);
    makeRequestStateless(getEmployeeListReq, buildFilterPayload(values, page), (response) => {
      const list = (response?.employee_employments?.data || []).map(mapEmployee);
      const meta = response?.employee_employments?.meta?.pagination || {};
      setAvailableEmployees(list);
      setEmployeePagination({
        current: meta.current_page || page,
        pageSize: meta.per_page || 10,
        total: meta.total || list.length,
      });

      if (props.employment_id) {
        const match = list.find((employee) => employee.id === props.employment_id);
        if (match) {
          setSelectedEmployees([match]);
        }
      }

      setEmployeesLoading(false);
    }, (err, res) => {
      setEmployeesLoading(false);
      message.error(res?.response?.data?.message || "Unable to load employees");
    });
  };

  const moveEmployee = (employee, targetPanel) => {
    if (targetPanel === "selected") {
      setSelectedEmployees((current) => (
        current.some((item) => item.id === employee.id)
          ? current
          : [...current, mapEmployee(employee)]
      ));
      return;
    }

    if (props.employment_id) {
      return;
    }

    setSelectedEmployees((current) => current.filter((item) => item.id !== employee.id));
    setAmountErrors((current) => {
      const nextErrors = new Set(current);
      nextErrors.delete(employee.id);
      return nextErrors;
    });
  };

  const moveAllToSelected = () => {
    setSelectedEmployees((current) => {
      const nextMap = new Map(current.map((employee) => [employee.id, employee]));
      unselectedEmployees.forEach((employee) => {
        nextMap.set(employee.id, nextMap.get(employee.id) || mapEmployee(employee));
      });
      return Array.from(nextMap.values());
    });
  };

  const moveAllToUnselected = () => {
    if (props.employment_id) {
      return;
    }
    setAmountErrors(new Set());
    setSelectedEmployees([]);
  };

  const updateSelectedAmount = (employeeId, value) => {
    setSelectedEmployees((current) => {
      const nextEmployees = current.map((employee) => (
        employee.id === employeeId ? { ...employee, amount: value ?? "" } : employee
      ));
      syncDefaultAmountField(nextEmployees);
      return nextEmployees;
    });
    setAmountErrors((current) => {
      const nextErrors = new Set(current);
      if (value === "" || value === null || value === undefined) {
        nextErrors.add(employeeId);
      } else {
        nextErrors.delete(employeeId);
      }
      return nextErrors;
    });
  };

  const onToggleDate = (date) => {
    setSelectedDates((current) => {
      const exists = current.includes(date);
      const nextDates = exists ? current.filter((item) => item !== date) : [...current, date];
      return nextDates.sort();
    });
  };

  const onMonthChange = (month) => {
    setCurrentMonth(month.clone().startOf("month"));
    setSelectedDates([]);
  };

  const onSelectDatePreset = (preset) => {
    const days = Array.from({ length: currentMonth.daysInMonth() }, (_, index) => currentMonth.clone().date(index + 1));
    const selected = days
      .filter((day) => preset === "weekends" ? [0, 6].includes(day.day()) : ![0, 6].includes(day.day()))
      .map((day) => day.format("YYYY-MM-DD"));
    setSelectedDates(selected);
  };

  const applyDefaultAmount = async () => {
    if (selectedEmployees.length === 0) {
      message.warning("Select at least one employee");
      return;
    }

    if (amountSource === "formula") {
      const formulaId = form.getFieldValue("formula_id");
      if (!formulaId) {
        message.warning("Select a formula first");
        return;
      }

      setApplyingAmount(true);
      setFormulaErrors([]);
      try {
        const response = await calculateFormulaReq({
          payitemId: props.payitemId,
          formula_id: formulaId,
          employment_ids: selectedEmployees.map((employee) => employee.id),
          month: currentMonth.format("YYYY-MM"),
          company_id: companyId,
        });

        if (response?.success === false || response?.response) {
          message.error(response?.message || response?.response?.data?.message || "Unable to calculate formula");
          return;
        }

        const calculatedItems = response?.data?.items || [];
        const errors = response?.data?.errors || [];
        const amountByEmployment = new Map(calculatedItems.map((item) => [String(item.employment_id), item.amount]));

        setSelectedEmployees((current) => current.map((employee) => (
          amountByEmployment.has(String(employee.id))
            ? { ...employee, amount: amountByEmployment.get(String(employee.id)) }
            : employee
        )));
        setFormulaErrors(errors);
        setAmountErrors(new Set(errors.map((error) => error.employment_id)));

        if (errors.length > 0) {
          message.warning(`${errors.length} employee formula amount${errors.length > 1 ? "s" : ""} could not be calculated`);
        } else {
          message.success("Formula amounts applied");
        }
      } catch (error) {
        message.error(error?.response?.data?.message || "Unable to calculate formula");
      } finally {
        setApplyingAmount(false);
      }
      return;
    }

    const defaultAmount = form.getFieldValue("default_amount");
    if (defaultAmount === undefined || defaultAmount === null || defaultAmount === "") {
      message.warning("Enter a default amount first");
      return;
    }

    setSelectedEmployees((current) => current.map((employee) => ({
      ...employee,
      amount: defaultAmount,
    })));
    setAmountErrors(new Set());
    message.success("Default amount applied");
  };

  const onApplyFilter = (values) => {
    setAppliedFilters(values);
    loadEmployees(values, 1);
  };

  const onResetFilter = () => {
    const nextValues = props.employment_id ? { employment_ids: [props.employment_id] } : {};
    filterForm.resetFields();
    filterForm.setFieldsValue(nextValues);
    setAppliedFilters(nextValues);
    loadEmployees(nextValues, 1);
  };

  const onChangeUnselectedPage = (page) => {
    loadEmployees(appliedFilters, page);
  };

  const validateStepOne = () => {
    if (selectedDates.length === 0) {
      message.error("Select at least one effective date");
      return false;
    }

    if (amountSource === "formula") {
      if (!form.getFieldValue("formula_id")) {
        message.error("Select a formula");
        return false;
      }
      return true;
    }

    const defaultAmount = form.getFieldValue("default_amount");
    if (defaultAmount === undefined || defaultAmount === null || defaultAmount === "") {
      message.error("Enter a default amount");
      return false;
    }

    return true;
  };

  const validateSelectedEmployees = () => {
    if (selectedEmployees.length === 0) {
      message.error("Select at least one employee");
      return false;
    }

    const invalidEmployees = selectedEmployees.filter((employee) => employee.amount === "" || employee.amount === null || employee.amount === undefined);
    if (invalidEmployees.length > 0) {
      setAmountErrors(new Set(invalidEmployees.map((employee) => employee.id)));
    } else {
      setAmountErrors(new Set());
    }

    const invalidEmployee = invalidEmployees[0];
    if (invalidEmployee) {
      message.error(`Apply or enter amount for ${invalidEmployee.full_name_code || invalidEmployee.full_name || invalidEmployee.employee_name}`);
      return false;
    }

    return true;
  };

  const goNext = () => {
    if (currentStep === 0 && !validateStepOne()) {
      return;
    }
    if (currentStep === 1 && !validateSelectedEmployees()) {
      return;
    }
    setCurrentStep((step) => Math.min(step + 1, 2));
  };

  const goBack = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const onConfirm = async () => {
    if (!validateStepOne() || !validateSelectedEmployees()) {
      return;
    }

    setSaving(true);
    try {
      const response = await bulkCreateDataReq({
        payitemId: props.payitemId,
        dates: selectedDates,
        source_type: amountSource,
        formula_id: amountSource === "formula" ? form.getFieldValue("formula_id") : null,
        description: form.getFieldValue("description"),
        processed: form.getFieldValue("processed") || false,
        company_id: companyId,
        items: selectedEmployees.map((employee) => ({
          employment_id: employee.id,
          amount: Number(employee.amount),
        })),
      });

      if (response?.success === false || response?.response) {
        message.error(response?.message || response?.response?.data?.message || "Unable to save records");
        return;
      }

      const created = response?.data?.created || 0;
      const updated = response?.data?.updated || 0;
      message.success(`Saved ${created + updated} custom payitem record${created + updated === 1 ? "" : "s"}`);
      props.onCompleted?.(true);
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to save records");
    } finally {
      setSaving(false);
    }
  };

  const renderStepOne = () => (
    <Form { ...formLayout } name={ formName } form={ form } initialValues={ { processed: false, amount_source: "fixed_amount" } }>
      <EffectiveDatesPicker
        currentMonth={ currentMonth }
        selectedDates={ selectedDates }
        onMonthChange={ onMonthChange }
        onToggleDate={ onToggleDate }
        onSelectPreset={ onSelectDatePreset }
        onClear={ () => setSelectedDates([]) }
      />

      <div style={ { marginTop: 18, padding: 18, border: "1px solid #E4E8EF", borderRadius: 8, background: "#FFFFFF" } }>
        <Row gutter={ 20 }>
          <Col lg={ 8 } md={ 12 } xs={ 24 }>
            <Form.Item name="amount_source" label={ fieldLabel("amount_source", "Amount Source") } className="da-mb-16">
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                onChange={ () => {
                  setAmountErrors(new Set());
                  setFormulaErrors([]);
                  setSelectedEmployees((current) => current.map((employee) => ({ ...employee, amount: "" })));
                } }
              >
                <Radio.Button value="fixed_amount">Fixed Amount</Radio.Button>
                <Radio.Button value="formula">Formula</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>

          { amountSource === "formula" ? (
            <Col lg={ 8 } md={ 12 } xs={ 24 }>
              <Form.Item name="formula_id" label={ fieldLabel("formula_id", "Select Formula") } className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select formula"
                  options={ formulaOptions }
                  loading={ formulasLoading }
                  onChange={ () => {
                    setFormulaErrors([]);
                    setAmountErrors(new Set());
                    setSelectedEmployees((current) => current.map((employee) => ({ ...employee, amount: "" })));
                  } }
                />
              </Form.Item>
            </Col>
          ) : (
            <Col lg={ 8 } md={ 12 } xs={ 24 }>
              <Form.Item name="default_amount" label={ fieldLabel("default_amount", "Default Amount") } className="da-mb-16">
                <InputNumber className="custom-payitem-amount-input" style={ { width: "100%" } } min={ 0 } placeholder="Default amount for selected employees" />
              </Form.Item>
            </Col>
          ) }

          <Col lg={ 8 } md={ 12 } xs={ 24 }>
            <Form.Item
              name="processed"
              label={ fieldLabel("processed", trans(`${langKey}.processed`)) }
              className="da-mb-16"
              valuePropName="checked"
            >
              <InputCheck disabled />
            </Form.Item>
          </Col>

          <Col span={ 24 }>
            <Form.Item name="description" label={ fieldLabel("description", trans(`${langKey}.description`)) } className="da-mb-0">
              <InputTextArea />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </Form>
  );

  const renderStepTwo = () => (
    <>
      <div style={ { padding: 20, border: "1px solid #C9D6DF", borderRadius: 8, background: "rgba(201, 214, 223, 0.05)", marginBottom: 24 } }>
        <Form
          { ...formLayout }
          name="customPayitemFilterForm"
          form={ filterForm }
          onFinish={ onApplyFilter }
          initialValues={ props.employment_id ? { employment_ids: [props.employment_id] } : {} }
        >
          <Row gutter={ 20 }>
            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="employment_ids" label={ fieldLabel("employment_ids", "Employment") } className="da-mb-16">
                <SelectResourceList type="employment" mode="multiple" disabled={ !!props.employment_id } />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="location_id" label={ fieldLabel("location_id", "Location") } className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Location"
                  options={ createSelectList(filterOptions.locations) }
                  loading={ filterLoading }
                  onChange={ () => filterForm.setFieldValue("branch_id", undefined) }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="designation_id" label={ fieldLabel("designation_id", "Designation") } className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Designation"
                  options={ createSelectList(filterOptions.designations) }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="grade_id" label={ fieldLabel("grade_id", "Grade") } className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Grade"
                  options={ createSelectList(filterOptions.grades) }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="department_id" label={ fieldLabel("department_id", "Department") } className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Department"
                  options={ createSelectList(filterOptions.departments, { key: "department", filterKey: "id" }) }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>

            <Col lg={ 8 } sm={ 12 } xs={ 24 }>
              <Form.Item name="branch_id" label={ fieldLabel("branch_id", "Branch") } className="da-mb-16">
                <SelectWithSearch
                  placeholder="Select Branch"
                  options={ branchOptions }
                  loading={ filterLoading }
                  disabled={ !!props.employment_id }
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={ { display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 16, borderTop: "1px solid #DCE7F3", flexWrap: "wrap" } }>
            <Button danger onClick={ onResetFilter } disabled={ !!props.employment_id }>Reset Filter</Button>
            <Button type="primary" htmlType="submit" loading={ employeesLoading } disabled={ !!props.employment_id }>Apply Filter</Button>
          </div>
        </Form>
      </div>

      { amountSource === "formula" && (
        <div style={ { border: "1px solid #D8E3F0", borderRadius: 8, background: "#FFFFFF", padding: 16, marginBottom: 16 } }>
          <div style={ { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" } }>
            <div style={ { flex: 1, minWidth: 260 } }>
              <Typography.Text type="secondary">Selected formula</Typography.Text>
              <Typography.Title level={ 5 } style={ { margin: "4px 0 8px", color: "#223052" } }>
                { selectedFormula?.name || selectedFormula?.label || "No formula selected" }
              </Typography.Title>
              <div style={ { border: "1px solid #EEF0F3", borderRadius: 8, padding: 12, background: "#F8FAFC", color: "#4B5563", wordBreak: "break-word", minHeight: 48 } }>
                { selectedFormula?.expression || "Formula expression is not available for this formula." }
              </div>
            </div>

            <Button
              type="primary"
              onClick={ applyDefaultAmount }
              disabled={ selectedEmployees.length === 0 || !selectedFormulaId }
              loading={ applyingAmount }
              style={ { minWidth: 210 } }
            >
              Apply amount by formula
            </Button>
          </div>
        </div>
      ) }

      <Spin spinning={ employeesLoading } tip="Loading...">
        <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 12, flexWrap: "wrap" } }>
          <Space wrap>
            <Tag color="blue">{ selectedEmployees.length } selected</Tag>
            <Tag color="orange">{ selectedDates.length } dates</Tag>
            { amountSource !== "formula" && (
              <Button onClick={ applyDefaultAmount } disabled={ selectedEmployees.length === 0 } loading={ applyingAmount }>
                Apply Default Amount
              </Button>
            ) }
          </Space>
          <Typography.Text type="secondary">
            Drag employee cards between sections or click a card to move it.
          </Typography.Text>
        </div>

        { formulaErrors.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={ { marginBottom: 14 } }
            message="Some formula amounts could not be calculated"
            description={ formulaErrors.slice(0, 4).map((error) => `${error.employee_name || "Employee"}: ${error.message}`).join(" | ") }
          />
        ) }

        <Row gutter={ 28 }>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <EmployeePanel
              title="Unselected Employees"
              count={ unselectedEmployees.length }
              panel="unselected"
              employees={ unselectedEmployees }
              onMove={ moveEmployee }
              onMoveAll={ moveAllToSelected }
              onAmountChange={ updateSelectedAmount }
              amountErrors={ amountErrors }
              pagination={ employeePagination }
              onPageChange={ onChangeUnselectedPage }
            />
          </Col>

          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <EmployeePanel
              title="Selected Employees"
              count={ selectedEmployees.length }
              panel="selected"
              employees={ selectedEmployees }
              onMove={ moveEmployee }
              onMoveAll={ moveAllToUnselected }
              onAmountChange={ updateSelectedAmount }
              amountErrors={ amountErrors }
            />
          </Col>
        </Row>
      </Spin>
    </>
  );

  const renderPreview = () => (
    <div>
      <div style={ { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 } }>
        <Tag color="blue">{ selectedEmployees.length } employees</Tag>
        <Tag color="orange">{ selectedDates.length } dates</Tag>
        <Tag color={ amountSource === "formula" ? "purple" : "green" }>
          { amountSource === "formula" ? `Formula: ${selectedFormula?.name || "Selected"}` : "Fixed amount" }
        </Tag>
        <Tag color="cyan">{ previewRows.length } records</Tag>
      </div>

      { amountSource === "formula" && (
        <Alert
          type="info"
          showIcon
          style={ { marginBottom: 16 } }
          message="Formula rows will update matching existing records"
          description="If the same pay item, employee, date, and formula already exists, its amount and description will be updated. Fixed amount rows always create new records."
        />
      ) }

      <Table
        size="small"
        dataSource={ previewRows }
        pagination={ { pageSize: 10 } }
        scroll={ { x: 860 } }
        columns={ [
          { title: "Employee", dataIndex: "employee_name", key: "employee_name" },
          { title: "Date", dataIndex: "date", key: "date", render: (date) => moment(date).format("DD MMM YYYY") },
          { title: "Amount", dataIndex: "amount", key: "amount", render: (amount) => Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 }) },
          { title: "Source", dataIndex: "source", key: "source" },
          { title: "Save Action", dataIndex: "action", key: "action" },
        ] }
      />
    </div>
  );

  const footer = [
    <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />,
    currentStep > 0 && <Button key="back_button" onClick={ goBack }>Back</Button>,
    currentStep < 2 && <Button key="next_button" type="primary" onClick={ goNext }>Next</Button>,
    currentStep === 2 && <Button key="confirm_button" type="primary" loading={ saving } onClick={ onConfirm }>Confirm</Button>,
  ].filter(Boolean);

  return (
    <ModalComponent
      top={ 20 }
      width={ 1300 }
      maskClosable={ false }
      mainTitle={ trans(Commons.create) }
      subTitle={ trans(`${langKey}.moduleName`) }
      visible={ true }
      footer={ footer }
      onCancel={ () => props.onCompleted(false) }
    >
      <style>
        { `
          .custom-payitem-amount-input.ant-input-number {
            height: 44px !important;
            padding: 0 !important;
          }

          .custom-payitem-amount-input .ant-input-number-input-wrap {
            height: 100% !important;
            display: flex !important;
            align-items: center !important;
          }

          .custom-payitem-amount-input .ant-input-number-input {
            height: 100% !important;
            line-height: 44px !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            display: block !important;
          }

          .custom-payitem-amount-input .ant-input-number-handler-wrap {
            height: 100% !important;
            top: 0 !important;
            bottom: 0 !important;
          }

          .custom-payitem-amount-input .ant-input-number-handler {
            height: 50% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
        ` }
      </style>
      <Steps
        current={ currentStep }
        style={ { marginBottom: 24 } }
        items={ [
          { title: "Details" },
          { title: "Employees" },
          { title: "Preview" },
        ] }
      />

      { currentStep === 0 && renderStepOne() }
      { currentStep === 1 && renderStepTwo() }
      { currentStep === 2 && renderPreview() }
    </ModalComponent>
  );
};

export default CreateCustomPayitem;

const formLayout = {
  layout: "vertical",
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
  labelAlign: "left",
};
